import { useMemo, useState } from "react";
import {
  View,
  Text,
  Pressable,
  Modal,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  Image,
} from "react-native";
import { Check, X } from "lucide-react-native";
import { getBlock, type ContentBlock } from "@src/packages/content";
import { useTranslations } from "@src/packages/i18n";
import TestDetailSheet from "../../../components/TestDetailSheet";
import ProcedureDetailSheet from "../../../components/ProcedureDetailSheet";
import { AppointmentDetailSheet } from "../modals/AppointmentDetailSheet";
import { localizeAppointmentTitle } from "../localizeAppointmentTitle";
import { Row, type RowSurface } from "@louise/moon-ui";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors } from "@louise/moon-ui/theme/tokens";
import type {
  AppointmentDetailItem,
  AppointmentScheduleItem,
} from "../schedule.types";

const BROWN = "#6B5548";
const TEXT = semanticColors.typography.primary;
const TEXT_LIGHT = semanticColors.typography.supporting;
const appointmentIconImage =
  require("../../../../assets/images/stethoscope-copper.png") as number;

type Props = {
  item: AppointmentScheduleItem & { date?: string };
  /** Match the surrounding surface when the row is rendered standalone. */
  surface?: RowSurface;
  /** Optional right-side label like "8:30 AM" or "Tomorrow" */
  dateLabel?: string;
  /** Show checkbox for tracking completion */
  trackable?: boolean;
  done?: boolean;
  onToggle?: (note?: string) => void;
  /** Called on press when this row has a custom action */
  onPress?: () => void;
  /** Called when user taps Edit in the factsheet detail view */
  onEdit?: (appt: AppointmentScheduleItem) => void;
  /** Note added via the checkbox "anything to remember" modal */
  checkboxNote?: string;
};

export default function AppointmentRow({
  item,
  surface = "plain",
  dateLabel,
  trackable = false,
  done = false,
  onToggle,
  onPress: externalOnPress,
  onEdit,
  checkboxNote,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("AppointmentRow");
  const [showAppointmentDetail, setShowAppointmentDetail] = useState(false);
  const [showEducationSheet, setShowEducationSheet] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");

  const factsheet = useMemo(() => {
    if (!item.factsheetId) return null;
    return getBlock(item.factsheetId) ?? null;
  }, [item.factsheetId]);
  const appointment = useMemo<AppointmentDetailItem | null>(
    () => (item.date ? { ...item, date: item.date } : null),
    [item],
  );
  const procedureFactsheet: ContentBlock<"procedure"> | null =
    factsheet?.kind === "procedure"
      ? (factsheet as ContentBlock<"procedure">)
      : null;
  const testFactsheet: ContentBlock<"test"> | ContentBlock<"product"> | null =
    factsheet?.kind === "test" || factsheet?.kind === "product"
      ? (factsheet as ContentBlock<"test"> | ContentBlock<"product">)
      : null;

  const handleCheckPress = () => {
    if (done) {
      onToggle?.();
    } else {
      setNoteText("");
      setShowNoteModal(true);
    }
  };

  const handleNoteSave = () => {
    setShowNoteModal(false);
    onToggle?.(noteText.trim() || undefined);
  };

  const handleNoteSkip = () => {
    setShowNoteModal(false);
    onToggle?.();
  };

  const rightLabel = dateLabel ?? item.time;
  const displayTitle = localizeAppointmentTitle(item.title, t);
  const handleRowPress =
    externalOnPress || appointment || factsheet
      ? () => {
          if (externalOnPress) {
            externalOnPress();
          } else if (appointment) {
            setShowAppointmentDetail(true);
          } else if (factsheet) {
            setShowEducationSheet(true);
          }
        }
      : undefined;

  const handleOpenEducation = () => {
    setShowAppointmentDetail(false);
    setShowEducationSheet(true);
  };

  const completionControl = trackable ? (
    <Pressable
      accessibilityRole="checkbox"
      accessibilityState={{ checked: done }}
      onPress={(event) => {
        event.stopPropagation?.();
        capturePress(pressLabel(displayTitle), "appointment_row_checkbox");
        handleCheckPress();
      }}
      hitSlop={8}
      style={{
        width: 34,
        alignItems: "flex-end",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <View
        style={{
          width: 24,
          height: 24,
          borderRadius: 12,
          backgroundColor: done ? BROWN : "transparent",
          borderWidth: done ? 0 : 1.5,
          borderColor: done ? BROWN : "rgba(0,0,0,0.12)",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {done ? <Check size={14} color="#fff" strokeWidth={2.5} /> : null}
      </View>
    </Pressable>
  ) : null;

  return (
    <>
      <Row
        surface={surface}
        leadingMeta={rightLabel}
        leading={
          <Image
            source={appointmentIconImage}
            style={{ width: 28, height: 28 }}
            resizeMode="contain"
          />
        }
        leadingMetaWidth={62}
        leadingSize={30}
        title={displayTitle}
        subtitle={item.subtitle}
        onPress={handleRowPress}
        // Row captures for itself; the visible title names the appointment.
        action="appointment_row"
        muted={done}
        trailing={completionControl}
        chevron={!completionControl && !!handleRowPress}
        paddingHorizontal={16}
        paddingVertical={12}
      />

      <AppointmentDetailSheet
        visible={showAppointmentDetail && appointment !== null}
        appointment={appointment}
        onClose={() => setShowAppointmentDetail(false)}
        onEdit={(nextAppointment) => {
          setShowAppointmentDetail(false);
          onEdit?.(nextAppointment);
        }}
        onOpenEducation={factsheet ? handleOpenEducation : undefined}
      />

      {showEducationSheet && procedureFactsheet && (
        <ProcedureDetailSheet
          item={procedureFactsheet}
          onClose={() => setShowEducationSheet(false)}
          appointment={
            checkboxNote
              ? {
                  ...item,
                  notes: item.notes
                    ? `${item.notes}\n---\nAppointment summary: ${checkboxNote}`
                    : `Appointment summary: ${checkboxNote}`,
                }
              : item
          }
          onEdit={onEdit}
        />
      )}

      {showEducationSheet && testFactsheet && (
        <TestDetailSheet
          item={testFactsheet}
          onClose={() => setShowEducationSheet(false)}
          returnTab="Care"
        />
      )}

      {/* Note modal — shown when checking off an appointment */}
      <Modal
        visible={showNoteModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowNoteModal(false)}
      >
        <Pressable
          onPress={() => {
            capturePress("dismiss_backdrop");
            setShowNoteModal(false);
          }}
          style={{
            flex: 1,
            backgroundColor: "rgba(0,0,0,0.4)",
            justifyContent: "center",
            alignItems: "center",
            padding: 24,
          }}
        >
          <KeyboardAvoidingView
            behavior={Platform.OS === "ios" ? "padding" : undefined}
          >
            <Pressable
              onPress={() => {}}
              style={{
                width: 320,
                maxWidth: "100%",
                padding: 20,
                borderRadius: 16,
                backgroundColor: "#FFFFFF",
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.12,
                shadowRadius: 24,
              }}
            >
              {/* Header */}
              <View
                className="flex-row items-center justify-between"
                style={{
                  marginBottom: 16,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 17, fontWeight: "700", color: TEXT }}
                >
                  {t("appointment_note.title")}
                </Text>
                <Pressable
                  onPress={() => {
                    capturePress("close_appointment_note");
                    setShowNoteModal(false);
                  }}
                  hitSlop={8}
                >
                  <X size={18} color={TEXT_LIGHT} />
                </Pressable>
              </View>

              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  color: TEXT_LIGHT,
                  marginBottom: 14,
                  lineHeight: 20,
                }}
              >
                {t("appointment_note.body")}
              </Text>

              <TextInput
                value={noteText}
                onChangeText={setNoteText}
                placeholder={t("appointment_note.placeholder")}
                placeholderTextColor="rgba(0,0,0,0.25)"
                multiline
                autoFocus
                style={{
                  backgroundColor: "rgba(0,0,0,0.03)",
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: "rgba(0,0,0,0.06)",
                  padding: 14,
                  fontSize: 14,
                  fontFamily: "DMSans_400Regular",
                  color: TEXT,
                  minHeight: 80,
                  textAlignVertical: "top",
                }}
              />

              {/* Actions */}
              <View
                className="flex-row"
                style={{
                  justifyContent: "flex-end",
                  gap: 12,
                  marginTop: 18,
                }}
              >
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(t("appointment_note.skip")),
                      "appointment_note.skip",
                    );
                    handleNoteSkip();
                  }}
                  style={{
                    paddingHorizontal: 16,
                    paddingVertical: 10,
                    borderRadius: 10,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      fontWeight: "600",
                      color: TEXT_LIGHT,
                    }}
                  >
                    {t("appointment_note.skip")}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(t("appointment_note.save")),
                      "appointment_note.save",
                    );
                    handleNoteSave();
                  }}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 10,
                    borderRadius: 10,
                    backgroundColor: BROWN,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      fontWeight: "600",
                      color: "#fff",
                    }}
                  >
                    {t("appointment_note.save")}
                  </Text>
                </Pressable>
              </View>
            </Pressable>
          </KeyboardAvoidingView>
        </Pressable>
      </Modal>
    </>
  );
}
